const divisors = {
  thousands: 1000,
  millions: 1000000,
  billions: 1000000000,
  trillions: 1000000000000,
  '': 1
};

const decimals = {
  thousands: 0,
  millions: 1,
  billions: 2,
  trillions: 3,
  '': 2
};

export const formatNumber = (value, unit, multiplier) => {
  if (!value) return '';
  let unitLower = (unit || '').toLowerCase();
  const multiplierLower = (multiplier || '').toLowerCase();

  const divisor = divisors[multiplierLower] || 1;
  const decimal = decimals[multiplierLower] || 2;

  if (unitLower.indexOf('%') > -1 || unitLower === 'percentage' || multiplierLower === 'percent' || value.toString().indexOf('%') > -1) {
    unitLower = 'percent';
  }

  if (unitLower.indexOf('us dollars') > -1 || unitLower.indexOf('usd') > -1){
    unitLower = 'us dollars';
  }

  switch (unitLower) {
    case 'us dollars':
    case 'currency':
      return Intl.NumberFormat('en-us', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal
      }).format(value / divisor);
    case 'percent':
      return Intl.NumberFormat('en-us', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value / 100);
    case 'time':
      // show minutes as hrs:min
      let hours = value / 60;
      let rhours = Math.floor(hours);
      let minutes = (hours - rhours) * 60;
      let rminutes = Math.round(minutes);
      return `${rhours}:${String(rminutes).padStart(2, '0')}`;
    default:
      return Intl.NumberFormat('en-us', {
        style: 'decimal',
        minimumFractionDigits: decimal,
        maximumFractionDigits: decimal
      }).format(value / divisor);
  }
};

export const formatNumberMulti = (value, units, multipliers) => {
  const unit = units.length <= 1 ? units[0] : '';
  const multiplier =
    multipliers.length <= 1 ? multipliers[0] : multipliers.reduce((maxMult, mult) => (divisors[mult] > divisors[maxMult] ? mult : maxMult), '');
  return formatNumber(value, unit, multiplier);
};

export const getYearsWithDatapoints = metrics => [
  ...new Set(metrics.reduce((years, metric) => years.concat(metric.datapoints.map(dp => dp.year)), []).sort((y1, y2) => y1 - y2))
];

export const getSelectedGeographies = (selectedMetric, geographies) => {
  const geos = (selectedMetric || {}).geographies || [];
  if (geos.length > 0) return geos;
  const us = geographies.find(g => g.name === 'US' || g.name === 'United States');
  if (us) return [{ selected: true, id: us.geographyId, name: us.name }];
  const first = geographies[0];
  if (first) return [{ selected: true, id: first.geographyId, name: first.name }];
  return [];
};

export const getInferredChartType = (value, unit) => {
  // Rare cases of percentage values greater than 100
  if (unit && unit.toLowerCase() === 'percentage') {
    return 'line';
  }
  if (unit && unit.toLowerCase() === 'time') {
    return 'bar';
  }
  else if (value <= 100) {
    return 'line';
  }
  else {
    return 'bar';
  }
}

export const getDatapointMultiplier = (datapoints, geographyId) => {
  let multiplier = '';
  let value = Math.abs(datapoints.find(d => d.geographies.geographyId === geographyId && d.value)?.value) || '';
  if(value) {
    if(value > 1000000000) {
      multiplier = 'billions';
    }
    else if(value > 1000000) {
      multiplier = 'millions';
    }
    else if(value > 1000) {
      multiplier = 'thousands';
    }
    else {
      if(datapoints.find(d => d.geographies.geographyId === geographyId && d.unit.toLowerCase() === 'time')) {
        multiplier = 'hr:min';
      }
      else {
        multiplier = '';
      }
    }
  }
  return multiplier
}

export const lexSort = (a, b) => {
  var aUpper = a.toUpperCase();
  var bUpper = b.toUpperCase();
  if (aUpper < bUpper) {
    return -1;
  }
  if (aUpper > bUpper) {
    return 1;
  }
  return 0;
};

// this function will upsert the given metric to the list of selected metrics
// if selectedMetrics contains the given metric, the `selected` and `geographies` properties are updated
// if selectedMetrics does not contain the given metric, a new entry is added with the provided `selected` and `geographies` property values
// if setGeoInURL is true, add the metric's geos to the url
export const upsertSelectedMetric = (metric, props, selectedMetrics, setGeoInURL) => {
  // Add the metric's geos to the url
  if (setGeoInURL) {
    let currentUrl = window.location.href;
    var urlBase = currentUrl.split('metric/')[0];
    var urlParams = currentUrl.split('metric/')[1];
    var newUrlParams = urlParams.split(',').map(p => {
      if (parseInt(p) === metric.id || p.includes(metric.id+"-")) {
        if(props.geographies) {
          var newGeos = props.geographies.map(g => geoList.find(geo => geo.id === g.id).shortName).join('_');
          if(newGeos && newGeos !== "") {
            return metric.id + "-" + newGeos;
          }
          else {
            return metric.id;
          }
        }
        return "";
      }
      return p;
    }).filter(p => p !== "").join(',');

    var newUrl = urlBase + 'metric/' + newUrlParams;
    window.history.pushState({}, null, newUrl);
  }

  let found = false;
  let updatedSelectedMetrics = selectedMetrics.map(m => {
    if (m.id === metric.id) found = true;
    
    return {
      selected: m.id === metric.id ? (props.selected !== undefined ? props.selected : m.selected) : m.selected,
      id: m.id,
      name: m.name,
      geographies: m.id === metric.id ? (props.geographies !== undefined ? props.geographies : m.geographies) : m.geographies
    };
  });
  if (!found)
    updatedSelectedMetrics.push({
      selected: props.selected !== undefined ? props.selected : false,
      id: metric.id,
      name: metric.name,
      geographies: props.geographies !== undefined ? props.geographies : null
    });
  return updatedSelectedMetrics;
};

export const resetUrlAfterDelete = (selectedMetrics) => {
  let currentUrl = window.location.href;
  var urlBase = currentUrl.split('metric/')[0];
  var urlParams = currentUrl.split('metric/')[1];
  var newUrlParams = urlParams.split(',').map(p => {
      if(selectedMetrics.some(m => m.id === parseInt(p.split("-")[0]))) {
        return p;
      } 
      return "";
  }).filter(p => p !== "").join(',');

  var newUrl = urlBase + 'metric/' + newUrlParams;
  window.history.pushState({}, null, newUrl);

}

export const getDatapointValue = (datapoints, year, geographyId) => 
  datapoints.filter(d => d.year === year && d.geographies && d.geographies.geographyId === geographyId).map(d => d.value)[0];

// This hardcoded geo list needs to match what's in the database and forecast file
export const geoList = [
  { id: "80", name: "Worldwide", shortName: "ww" },
  { id: "135", name: "Mexico", shortName: "mex" },
  { id: "168", name: "Peru", shortName: "per" },
  { id: "226", name: "Venezuela", shortName: "ven" },
  { id: "274", name: "Central & Eastern Europe", shortName: "ceeuro" },
  { id: "53", name: "Czech Republic", shortName: "cze" },
  { id: "95", name: "Hungary", shortName: "hun" },
  { id: "170", name: "Poland", shortName: "pol" },
  { id: "177", name: "Romania", shortName: "rou" },
  { id: "178", name: "Russia", shortName: "ru" },
  { id: "193", name: "Slovakia", shortName: "svk" },
  { id: "154", name: "North America", shortName: "na" },
  { id: "215", name: "Turkey", shortName: "tr" },
  { id: "218", name: "Ukraine", shortName: "ukr" },
  { id: "230", name: "Western Europe", shortName: "weuro" },
  { id: "15", name: "Austria", shortName: "aut" },
  { id: "21", name: "Belgium", shortName: "bel" },
  { id: "54", name: "Denmark", shortName: "dnk" },
  { id: "70", name: "Finland", shortName: "fin" },
  { id: "72", name: "France", shortName: "fr" },
  { id: "77", name: "Germany", shortName: "de" },
  { id: "82", name: "Greece", shortName: "grc" },
  { id: "35", name: "Canada", shortName: "can" },
  { id: "102", name: "Ireland", shortName: "irl" },
  { id: "104", name: "Italy", shortName: "it" },
  { id: "145", name: "Netherlands", shortName: "nld" },
  { id: "157", name: "Norway", shortName: "nor" },
  { id: "171", name: "Portugal", shortName: "prt" },
  { id: "200", name: "Spain", shortName: "esp" },
  { id: "205", name: "Sweden", shortName: "swe" },
  { id: "206", name: "Switzerland", shortName: "che" },
  { id: "220", name: "UK", shortName: "uk" },
  { id: "13", name: "Asia-Pacific", shortName: "ap" },
  { id: "221", name: "United States", shortName: "us" },
  { id: "14", name: "Australia", shortName: "aus" },
  { id: "43", name: "China", shortName: "cn" },
  { id: "94", name: "Hong Kong", shortName: "hk" },
  { id: "97", name: "India", shortName: "ind" },
  { id: "98", name: "Indonesia", shortName: "idn" },
  { id: "107", name: "Japan", shortName: "jpn" },
  { id: "128", name: "Malaysia", shortName: "mys" },
  { id: "147", name: "New Zealand", shortName: "nz" },
  { id: "169", name: "Philippines", shortName: "phl" },
  { id: "192", name: "Singapore", shortName: "sgp" },
  { id: "115", name: "Latin America", shortName: "la" },
  { id: "198", name: "South Korea", shortName: "kor" },
  { id: "208", name: "Taiwan", shortName: "twn" },
  { id: "210", name: "Thailand", shortName: "tha" },
  { id: "227", name: "Vietnam", shortName: "vnm" },
  { id: "3", name: "Middle East & Africa", shortName: "meafr" },
  { id: "61", name: "Egypt", shortName: "egy" },
  { id: "103", name: "Israel" , shortName: "isr" }, 
  { id: "110", name: "Kenya", shortName: "ken" },
  { id: "112", name: "Kuwait", shortName: "kwt" },
  { id: "117", name: "Lebanon", shortName: "lbn" },
  { id: "10", name: "Argentina", shortName: "arg" },
  { id: "150", name: "Nigeria", shortName: "nga" },
  { id: "186", name: "Saudi Arabia", shortName: "sau" },
  { id: "196", name: "South Africa", shortName: "zaf" },
  { id: "219", name: "United Arab Emirates", shortName: "are" },
  { id: "284", name: "Other Latin America", shortName: "ola" },
  { id: "306", name: "EU-5", shortName: "euro" },
  { id: "288", name: "Other Western Europe", shortName: "oeuro" },
  { id: "282", name: "Other Asia-Pacific", shortName: "oap" },
  { id: "283", name: "Other Central & Eastern Europe", shortName: "oceeuro" },
  { id: "304", name: "Southeast Asia", shortName: "seasia" },
  { id: "29", name: "Brazil", shortName: "br" },
  { id: "42", name: "Chile", shortName: "chl" },
  { id: "44", name: "Colombia", shortName: "col" }
];